export interface SimilarityWeights {
  keywordsW1Weight: number;
  keywordsW2Weight: number;
  keywordsW3Weight: number;
}

// Field identifiers for ordering: tracks.v4 keywords bucketed by weight (3 = strongest)
export type SimilarityField = 'keywordsW3' | 'keywordsW2' | 'keywordsW1';

export type DropTokensMode = 'right_to_left' | 'left_to_right';

export type SimilarityFieldsEnabled = Record<SimilarityField, boolean>;

// Default field order matches extractStylesFromTrack in similarity-queries.ts
// Strongest first, so right_to_left token dropping sheds weak keywords first.
export const DEFAULT_FIELD_ORDER: SimilarityField[] = [
  'keywordsW3',
  'keywordsW2',
  'keywordsW1',
];

export const DEFAULT_DROP_TOKENS_MODE: DropTokensMode = 'right_to_left';

// Default values match the production constants in similarity-queries.ts
export const DEFAULT_SIMILARITY_WEIGHTS: SimilarityWeights = {
  keywordsW3Weight: 15,
  keywordsW2Weight: 10,
  keywordsW1Weight: 5,
};

// Default fields enabled state (all enabled)
export const DEFAULT_FIELDS_ENABLED: Record<SimilarityField, boolean> = {
  keywordsW3: true,
  keywordsW2: true,
  keywordsW1: true,
};

// Helper to generate the query_by_weights string from weights.
// Positional: must stay in SIMILARITY_QUERY_BY order (w3, w2, w1).
export const generateQueryByWeights = (weights: SimilarityWeights): string =>
  [
    weights.keywordsW3Weight,
    weights.keywordsW2Weight,
    weights.keywordsW1Weight,
  ].join(',');

export interface SfxSimilarityWeights {
  keywordsWeight: number;
  nameWeight: number;
  stylesWeight: number;
}

// SFX field identifiers for ordering
export type SfxSimilarityField = 'name' | 'keywords' | 'styles';

export type SfxSimilarityFieldsEnabled = Record<SfxSimilarityField, boolean>;

// Default SFX field order matches extractKeywordsFromSfx in sfx-similarity-queries.ts
export const DEFAULT_SFX_FIELD_ORDER: SfxSimilarityField[] = [
  'name',
  'keywords',
  'styles',
];

// Default values match the production constants in sfx-similarity-queries.ts
export const DEFAULT_SFX_SIMILARITY_WEIGHTS: SfxSimilarityWeights = {
  keywordsWeight: 1,
  stylesWeight: 5,
  nameWeight: 2,
};

// Default SFX fields enabled state (all enabled)
export const DEFAULT_SFX_FIELDS_ENABLED: Record<SfxSimilarityField, boolean> = {
  name: true,
  keywords: true,
  styles: true,
};

// Helper to generate SFX query_by_weights string from weights
export const generateSfxQueryByWeights = (
  weights: SfxSimilarityWeights
): string =>
  [
    `keywords:${weights.keywordsWeight}`,
    `styles:${weights.stylesWeight}`,
    `name:${weights.nameWeight}`,
  ].join(', ');
